import { useFlags } from '@atlaskit/flag';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useEffect, useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { RunsApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { AddEditRunFormFields } from 'core/config/form-fields';
import { IAddEditRunFormOutput } from '../types';
import { IRunDao, IRunDto } from 'core/api/types/runs.interface';

const AddEditRunsDialog = ({ run }: { run?: IRunDao }) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();

  const [formFields, setFormFields] = useState<ISharedFormField[]>(AddEditRunFormFields);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const getDefaultValue = (run: IRunDao, key: string) => {
    switch (key) {
      default:
        return run[key as keyof IRunDao];
    }
  };

  useEffect(() => {
    const fields = AddEditRunFormFields.map((field) => ({
      ...field,
      defaultValue: run ? getDefaultValue(run, field.key) : field.defaultValue,
    }));

    setFormFields(fields as ISharedFormField[]);
  }, [run]);

  const addEditRun = async (data: IAddEditRunFormOutput) => {
    setFormSubmitting(true);

    const payload: IRunDto = {
      active: data.active,
      name: data.name,
      notes: data.notes,
      locations: run?.locations || [],
    };

    try {
      if (run) {
        await RunsApiService.update(run.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await RunsApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }
      showSuccessFlag(
        run ? 'Run updated' : 'Run created',
        `The run has been ${run ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        run ? 'Run update failed' : 'Run creation failed',
        `Unable to ${run ? 'update' : 'create'} a run, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const handleFormChange = (data: any) => {
    if (data.options) {
      setFormFields(
        formFields.map((field) =>
          field.key === 'requireComment' ? { ...field, isDisabled: !data.options.length, options: data.options } : field
        )
      );
    }
  };

  const customContent = () => {
    return (
      <>
        <SharedForm
          className='p-4 overflow-y-auto'
          onSubmit={addEditRun}
          onChange={handleFormChange}
          fields={formFields as ISharedFormField[]}
          buttonLabel='Submit'
          loading={formSubmitting}
          cancelButton={cancelButton}
        />
      </>
    );
  };

  return (
    <SharedDialogBase
      title={run ? 'Update run' : 'Add run'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditRunsDialog;
